<template>
  <div>
    <q-toolbar color="white" text-color="grey-9" class="top-page-toolbar">
      <q-toolbar-title class="q-body-2">
        <router-link class="text-grey-10 no-underline" :to="{ name: 'expenses-dashboard', params: { teamId: activeTeam.id } }">
          {{ $tc('expense', 2) }}
        </router-link> / <span class="text-weight-bold">{{ $t('capture_expense') }}</span>
      </q-toolbar-title>
      <div v-if="!allCaptured">
        <q-btn
          :disabled="!hasPrevious"
          icon="navigate_before"
          class="q-mr-sm"
          flat
          dense
          @click="handlePrevious"
        />
        <q-btn
          :disabled="!hasNext"
          icon="navigate_next"
          class="q-mr-lg"
          flat
          dense
          @click="handleNext"
        />
        <q-btn
          color="primary"
          class="q-mr-md"
          flat
          icon="delete"
          @click="handleDelete"
        >
          {{ $t('delete') }}
        </q-btn>
        <q-btn
          color="primary"
          @click="handleSave"
        >
        {{ $t('save') }}
        </q-btn>
      </div>
    </q-toolbar>
    <div class="layout-padding" style="min-height: calc(100vh - 60px - 70px)">
      <div v-if="!allCaptured" class="row full-height">
        <div class="col-12 col-md-6">
          <document-viewer v-if="document" :document="document" :showDownloadButton="true" />
        </div>
        <div class="col-12 col-md-6">
          <expense-form v-model="formData" />
        </div>
      </div>
      <div v-else class="flex column text-center">
        <img src="~assets/emptystates/items_empty.svg" />
        <h6 class="q-my-md text-faded">{{ $t('no_expenses_to_capture') }}</h6>
        <div>
          <q-btn @click="$router.push({ name: 'expenses-dashboard' })" color="primary" class="q-mt-lg" outline v-is-user>
            {{ $t('back_to_expense_dashboard') }}
          </q-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { Dialog, date } from 'quasar'
  import { get, isNil, omitBy } from 'lodash'
  import { mapActions, mapGetters } from 'vuex'
  import DocumentViewer from 'components/Documents/DocumentViewer.vue'
  import ExpenseForm from 'components/Expenses/ExpenseForm.vue'
  import { ExpensesClient } from 'src/api/Expenses'
  import { createErrorFromResponse } from 'src/utils/error'
  import { general } from 'src/config'
  import i18n from 'src/i18n/i18n'
  import parseDate from 'date-fns/parse'
  import { snakeCase } from 'change-case-object'

  const includes = [
    'supplier', 'document', 'document.documentRecognition', 'document.documentRecognition.customer'
  ]

  export default {
    $_veeValidate: {
      validator: 'new'
    },
    components: {
      DocumentViewer,
      ExpenseForm
    },
    data () {
      return {
        expense: null,
        meta: null,
        expenseLoaded: false,
        formData: {
          currency: null
        }
      }
    },
    computed: {
      ...mapGetters(['activeTeam']),
      document () {
        if (this.expense === null) {
          return null
        }

        return this.expense.document.data
      },
      hasPrevious () {
        if (this.meta === null) {
          return false
        }

        return this.meta.pagination.current_page !== 1 &&
          this.meta.pagination.total_pages !== 1
      },
      hasNext () {
        if (this.meta === null) {
          return false
        }

        return this.meta.pagination.current_page !== this.meta.pagination.total_pages
      },
      allCaptured () {
        if (this.meta === null) {
          return false
        }

        return this.expenseLoaded && this.meta.pagination.total === 0
      }
    },
    methods: {
      ...mapActions(['deleteExpense']),

      resetFormData () {
        this.formData = null
      },
      handlePrevious () {
        if (!this.hasPrevious) {
          return
        }

        this.fetchPrevious()
      },
      handleNext () {
        if (!this.hasNext) {
          return
        }

        this.fetchNext()
      },
      fetchCurrent () {
        return new ExpensesClient()
          .page(this.meta.pagination.current_page)
          .limit(1)
          .filter([['complete', '!=', 'true']])
          .sort('created_at:asc')
          .include(includes)
          .list()
          .then((response) => {
            const pagination = response.data.meta.pagination
            if (
              this.meta.pagination.current_page > pagination.total_pages &&
              pagination.total !== 0
            ) {
              this.meta = response.data.meta
              this.meta.pagination.current_page = pagination.total_pages
              return this.fetchCurrent()
            } else {
              this.handleResponse(response)
            }
          })
      },
      fetchPrevious () {
        return new ExpensesClient()
          .page(this.meta.pagination.current_page - 1)
          .limit(1)
          .filter([['complete', '!=', 'true']])
          .sort('created_at:asc')
          .include(includes)
          .list()
          .then(this.handleResponse)
      },
      fetchNext () {
        return new ExpensesClient()
          .page(this.meta.pagination.current_page + 1)
          .limit(1)
          .filter([['complete', '!=', 'true']])
          .sort('created_at:asc')
          .include(includes)
          .list()
          .then(this.handleResponse)
      },
      handleResponse (response) {
        const expense = response.data.data[0]
        const meta = response.data.meta
        const recognition = get(expense, 'document.data.documentRecognition.data', null)

        this.expense = expense
        this.meta = meta
        this.expenseLoaded = true

        const invoiceDate = expense.invoice_date
          ? expense.invoice_date.data
          : get(recognition, 'result.entities.issuedAt', null)

        this.formData.invoiceDate = invoiceDate ? parseDate(invoiceDate) : null
        this.formData.supplier = expense.supplier || get(recognition, 'customer.data', null)
        this.formData.invoiceNumber = expense.invoice_number || get(recognition, 'result.entities.number', null)
        this.formData.currency = expense.currency || 'EUR'
        this.formData.totalNet = this.getTotalNet(expense, recognition)
      },
      getTotalNet (expense, recognition) {
        if (expense.total_net) {
          return expense.total_net
        }

        const rec = get(recognition, 'result.entities.totals.net', null)

        return rec === null ? null : Math.floor(rec * 100)
      },

      handleSave () {
        this.$validator.validateAll()
          .then(result => {
            if (result) {
              this.updateExpense()
            }
          })
      },
      updateExpense () {
        const data = {
          ...snakeCase(this.formData),
          invoice_date: this.formData.invoiceDate
            ? date.formatDate(this.formData.invoiceDate, 'YYYY-MM-DD')
            : null,
          delivery_date: this.formData.deliveryDate
            ? date.formatDate(this.formData.deliveryDate, 'YYYY-MM-DD')
            : null,
          due_date: this.formData.dueDate
            ? date.formatDate(this.formData.dueDate, 'YYYY-MM-DD')
            : null,
          accounting_period: this.formData.accountingPeriod
            ? date.formatDate(this.formData.accountingPeriod, 'YYYY-MM-DD')
            : null,
          cash_discount_limit: this.formData.cashDiscountLimit
            ? date.formatDate(this.formData.cashDiscountLimit, 'YYYY-MM-DD')
            : null,
          paid_at: this.formData.paidAt
            ? date.formatDate(this.formData.paidAt, 'YYYY-MM-DD')
            : null,
          'supplier_id': this.formData.supplier ? this.formData.supplier.id : null,
          'lines': !isNil(this.formData.salesTaxRate) && !isNil(this.formData.salesTaxAmount)
            ? [ { 'total_net': this.formData.totalNet, 'tax_rate': this.formData.salesTaxRate, 'tax_amount': this.formData.salesTaxAmount } ]
            : null,
          complete: true
        }

        delete data.supplier
        delete data.sales_tax_rate
        delete data.sales_tax_amount

        const cleanedData = omitBy(data, isNil)

        new ExpensesClient()
          .update(this.expense.id, cleanedData)
          .then(response => {
            this.$q.notify({ message: this.$t('expense_captured_success'), type: 'positive' })
          })
          .then(() => {
            return this.fetchCurrent()
          }).then(() => {
            this.resetFormData()
          })
          .catch(createErrorFromResponse)
      },
      handleDelete () {
        Dialog.create({
          title: i18n.t('delete_expense_dialog_title'),
          message: i18n.t('delete_expense_dialog_body'),
          color: 'primary',
          ok: i18n.t('delete'),
          cancel: i18n.t('cancel')
        })
          .then(() => {
            this.deleteExpense({ expenseId: this.expense.id })
              .then(() => {
                this.resetFormData()
              })
              .then(() => {
                if (this.hasNext) {
                  return this.fetchNext()
                } else if (this.hasPrevious) {
                  return this.fetchPrevious()
                }
              })
          })
          .catch(() => {})
      }
    },
    beforeRouteEnter (to, from, next) {
      new ExpensesClient()
        .limit(1)
        .filter([['complete', '!=', 'true']])
        .sort('created_at:asc')
        .include(includes)
        .list()
        .then((response) =>
          next((vm) => vm.handleResponse(response))
        )
    },
    beforeRouteUpdate (to, from, next) {
      new ExpensesClient()
        .limit(1)
        .filter([['complete', '!=', 'true']])
        .sort('created_at:asc')
        .include('document')
        .list()
        .then(this.handleResponse)
    },
    created () {
      this.formData.currency = general.base_currency
    }
  }
</script>
